import {
  IsNotEmpty,
  IsOptional,
  IsString,
  IsEnum,
  IsUUID,
  IsObject,
  IsArray,
  MaxLength,
  ValidateNested,
} from 'class-validator'
import { Type } from 'class-transformer'
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import { Transform } from 'class-transformer'
import { ResourceType } from '../../../types/resource.types'
import {
  PedagogicContentState,
  ContentVisibility,
} from '../../../enums/content.enum'
import {
  MAX_RESOURCE_TITLE_LENGTH,
  MAX_RESOURCE_DESCRIPTION_LENGTH,
  MAX_URL_LENGTH,
} from '../../../constants/resource.constants'
import {
  toStringOrUndefined,
  parseJsonOrDefault,
} from '../../../utils/transformers.util'
import { CreateResourceLinkDto } from '../../resources-link/request/create-resource-link.dto'

/**
 * DTO for creating a new resource
 */
export class CreateResourceDto {
  @ApiPropertyOptional({
    description: 'Titre de la ressource',
    example: 'Chapitre 1 - Introduction aux mathématiques',
    maxLength: MAX_RESOURCE_TITLE_LENGTH,
    type: 'string',
  })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_RESOURCE_TITLE_LENGTH)
  @Transform(toStringOrUndefined)
  title?: string

  @ApiPropertyOptional({
    description: 'Description de la ressource',
    example: 'Document PDF contenant les exercices du chapitre 1',
    maxLength: MAX_RESOURCE_DESCRIPTION_LENGTH,
    type: 'string',
  })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_RESOURCE_DESCRIPTION_LENGTH)
  @Transform(toStringOrUndefined)
  description?: string

  @ApiProperty({
    description: 'URL de la ressource',
    example: 'documents/math-chapitre1.pdf',
    maxLength: MAX_URL_LENGTH,
    type: 'string',
  })
  @IsNotEmpty({ message: "L'URL de la ressource est requise" })
  @IsString()
  @MaxLength(MAX_URL_LENGTH)
  url!: string

  @ApiProperty({
    description: 'Type de ressource',
    enum: ResourceType,
    example: ResourceType.FILE,
  })
  @IsNotEmpty({ message: 'Le type de ressource est requis' })
  @IsEnum(ResourceType)
  type!: ResourceType

  @ApiPropertyOptional({
    description: 'ID de la leçon associée (rétrocompatibilité, préférer links)',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  @Transform(toStringOrUndefined)
  lessonId?: string

  @ApiPropertyOptional({
    description: 'ID du chapitre associé (rétrocompatibilité, préférer links)',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  @Transform(toStringOrUndefined)
  chapterId?: string

  @ApiPropertyOptional({
    description: 'Liens vers plusieurs chapitres et/ou leçons',
    type: () => [CreateResourceLinkDto],
    example: [
      { chapterId: '550e8400-e29b-41d4-a716-446655440000' },
      { lessonId: '660e8400-e29b-41d4-a716-446655440001' },
    ],
  })
  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => CreateResourceLinkDto)
  links?: CreateResourceLinkDto[]

  @ApiPropertyOptional({
    description: 'Métadonnées de la ressource (JSON)',
    example: { author: 'John Doe', pages: 10 },
  })
  @IsOptional()
  @IsObject()
  @Transform(parseJsonOrDefault)
  metadata?: Record<string, any>

  @ApiPropertyOptional({
    description: 'État de la ressource',
    enum: PedagogicContentState,
    default: PedagogicContentState.ACTIVE,
  })
  @IsOptional()
  @IsEnum(PedagogicContentState)
  state?: PedagogicContentState

  @ApiPropertyOptional({
    description: 'Visibilité de la ressource',
    enum: ContentVisibility,
    default: ContentVisibility.PRIVATE,
  })
  @IsOptional()
  @IsEnum(ContentVisibility)
  visibility?: ContentVisibility
}
